import { Waves, Minus, Plus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { usePlayer } from "@/context/PlayerContext";
import { CROSSFADE_MAX } from "@/lib/playback";
import { OFFSET_MAX, OFFSET_STEP, clampOffset, fmtOffset } from "@/lib/lyrics";

export const PlaybackSection = () => {
  const { playback, setPlayback } = usePlayer();
  const cf = playback.crossfade;
  const lo = playback.lyricsOffset || 0;
  const nudge = "flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 text-zinc-700 transition-colors hover:border-zinc-900 disabled:opacity-30";
  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-8" data-testid="playback-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-rose-50 text-rose-700"><Waves size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Playback</h2>
          <p className="text-xs text-zinc-500">The next song is loaded ahead of time so songs flow into each other without a pause.</p>
        </div>
      </div>
      <div className="mt-6 space-y-6">
        <div data-testid="crossfade-row">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-800">Crossfade</p>
              <p className="text-xs text-zinc-500">Blend the end of one song into the start of the next.</p>
            </div>
            <span className="font-mono text-sm text-zinc-700" data-testid="crossfade-value">{cf ? `${cf} s` : "Off"}</span>
          </div>
          <Slider value={[cf]} min={0} max={CROSSFADE_MAX} step={1} onValueChange={([v]) => setPlayback({ crossfade: v })} className="mt-4" data-testid="crossfade-slider" aria-label="Crossfade seconds" />
          <div className="mt-1 flex justify-between font-mono text-[10px] text-zinc-400"><span>Off</span><span>{CROSSFADE_MAX} s</span></div>
        </div>
        <div className="flex items-center justify-between gap-4" data-testid="gapless-row">
          <div>
            <p className="text-sm text-zinc-800">Gapless for albums</p>
            <p className="text-xs text-zinc-500">Songs that follow each other on the same album play seamlessly with no fade — for live albums, mixes and concept albums. Crossfade still applies between albums, playlists and shuffle.</p>
          </div>
          <Switch checked={playback.gaplessAlbums} onCheckedChange={(v) => setPlayback({ gaplessAlbums: v })} data-testid="gapless-switch" />
        </div>
        <div className="flex items-center justify-between gap-4" data-testid="lyrics-offset-row-settings">
          <div>
            <p className="text-sm text-zinc-800">Lyrics timing</p>
            <p className="text-xs text-zinc-500">Default nudge for synced lyrics on every song (+ shows lines earlier, − later). Each song can still be fine-tuned in Now Playing.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setPlayback({ lyricsOffset: clampOffset(lo - OFFSET_STEP) })} disabled={lo <= -OFFSET_MAX} className={nudge} data-testid="settings-lyrics-offset-minus" aria-label="Lyrics later"><Minus size={14} /></button>
            <span className="w-14 text-center font-mono text-sm text-zinc-700" data-testid="settings-lyrics-offset-value">{fmtOffset(lo)}</span>
            <button type="button" onClick={() => setPlayback({ lyricsOffset: clampOffset(lo + OFFSET_STEP) })} disabled={lo >= OFFSET_MAX} className={nudge} data-testid="settings-lyrics-offset-plus" aria-label="Lyrics earlier"><Plus size={14} /></button>
          </div>
        </div>
      </div>
    </section>
  );
};
